import { useQuery } from "@tanstack/react-query";
import { EvtStatsCard } from "components/stats/display-card";
import { EvtStatsCardPlaceholder } from "components/stats/placeholder";
import QueryError from "components/query-error";
import { formatRelativeDate } from "utils/utils";
import { formatHoursToTime } from "utils/timesheet";
import { fetchBranchSummary } from "./api";

// Hours since the date, formatted like HoursCell (formatHoursToTime): "3h ago".
const hoursAgo = (date) => {
  const d = date ? new Date(date) : null;
  if (!d || Number.isNaN(d.getTime())) return "-";
  const hours = Math.max(0, (Date.now() - d.getTime()) / 3600000);
  return `${formatHoursToTime(hours, { hoursOnly: true })} ago`;
};

const GRID = "grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 px-4 gap-4 md:px-6 mt-2.5";

// Stat cards, same components and grid as the job analytics dashboard.
export default function RepoBranchStats({ repositoryUrl }) {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["integrations-github-branch-summary", repositoryUrl],
    queryFn: () => fetchBranchSummary({ repository_url: repositoryUrl }),
    enabled: !!repositoryUrl,
  });

  // Also shimmer while the repo (and so its URL) is still loading and the query is disabled.
  if (isLoading || !repositoryUrl) {
    return (
      <div className={GRID}>
        {[0, 1, 2, 3].map((i) => (
          <EvtStatsCardPlaceholder key={i} />
        ))}
      </div>
    );
  }
  if (isError) return <QueryError message="Failed to load branches." onRetry={refetch} />;

  const items = data?.items || [];
  const latest = [...items].sort((a, b) => new Date(b.last_commit?.date || 0) - new Date(a.last_commit?.date || 0))[0];
  const defaultBranch = items.find((b) => b.is_default);
  const committers = new Set(
    items.map((b) => b.last_commit?.author?.github_username || b.last_commit?.author?.name).filter(Boolean),
  ).size;

  return (
    <div className={GRID}>
      <EvtStatsCard
        label="Branches"
        value={data?.total ?? items.length}
        description={`${items.filter((b) => b.is_protected).length} protected`}
      />
      <EvtStatsCard
        label="Default branch"
        value={defaultBranch?.name || "-"}
        description={formatRelativeDate(defaultBranch?.last_commit?.date) || "-"}
      />
      <EvtStatsCard label="Last activity" value={hoursAgo(latest?.last_commit?.date)} description={latest?.name} />
      <EvtStatsCard label="Recent committers" value={committers} description="Across branch heads" />
    </div>
  );
}
